import { forwardRef, useEffect, useImperativeHandle } from "react"
import { Group, Paper, Select, SimpleGrid, TextInput, Text, Stack, Tabs, FileInput, ActionIcon, Image, Box } from "@mantine/core"
import { useForm } from "@mantine/form"
import { useTranslation } from "react-i18next"
import { notifications } from "@mantine/notifications"
import { useCreateAgency, useUpdateAgency, useMasterAgencies, useUploadMultimedia, useRemoveMultimedia } from "../hooks/useAgencies"
import type { AgencyDetail, AgencyFormValues } from "../models/agency"
import { DISTRICTS_PT, DISTRICTS_BR } from "../models/agency"
import { env } from "../../../env"

interface Props {
  agency?: AgencyDetail
  onSuccess: () => void
}

const AgencyFormContent = forwardRef<{ submit: () => void }, Props>(({ agency, onSuccess }, ref) => {
  const { t } = useTranslation()
  const isEdit = !!agency
  const createMutation = useCreateAgency()
  const updateMutation = useUpdateAgency()
  const uploadMultimedia = useUploadMultimedia()
  const removeMultimedia = useRemoveMultimedia()
  const { data: masterAgencies } = useMasterAgencies()

  const districts = env.isGeaBrasil ? DISTRICTS_BR : DISTRICTS_PT
  const districtOptions = districts.map((d) => ({ value: String(d.value), label: d.label }))
  const masterOptions = (masterAgencies ?? []).map((a) => ({ value: String(a.id), label: a.name }))

  const form = useForm<AgencyFormValues>({
    initialValues: {
      name: "",
      social_denomination: "",
      district: null,
      RNAVT: "",
      nif: "",
      address: "",
      postal_code: "",
      locale: "",
      phone_number: "",
      email: "",
      status: 10,
      is_master: true,
      agency_id: null,
      subdomain: "",
      facebook_url: "",
      instagram_url: "",
      bd_egoi: "",
      accession_date: "",
      representative: "",
      color: "",
    },
    validate: {
      name: (v) => !v ? t("agencies.validation.nameRequired") : null,
      RNAVT: (v) => !v ? t("agencies.validation.RNAVTRequired") : null,
      nif: (v) => !v ? t("agencies.validation.nifRequired") : null,
      address: (v) => !v ? t("agencies.validation.addressRequired") : null,
      postal_code: (v) => !v ? t("agencies.validation.postalCodeRequired") : null,
      locale: (v) => !v ? t("agencies.validation.localeRequired") : null,
      email: (v) => !v ? t("agencies.validation.emailInvalid") : /^[^\s@]+@[^\s@]+$/.test(v) ? null : t("agencies.validation.emailInvalid"),
    },
  })

  useEffect(() => {
    if (agency) {
      form.setValues({
        name: agency.name,
        social_denomination: agency.social_denomination ?? "",
        district: agency.district,
        RNAVT: agency.RNAVT,
        nif: agency.nif,
        address: agency.address,
        postal_code: agency.postal_code,
        locale: agency.locale,
        phone_number: agency.phone_number ?? "",
        email: agency.email,
        status: agency.status,
        is_master: agency.is_master,
        agency_id: agency.agency_id,
        subdomain: agency.subdomain ?? "",
        facebook_url: agency.facebook_url ?? "",
        instagram_url: agency.instagram_url ?? "",
        bd_egoi: agency.bd_egoi ?? "",
        accession_date: agency.accession_date ?? "",
        representative: agency.representative ?? "",
        color: agency.color ?? "",
      })
    }
  }, [agency])

  const handleSubmit = (values: AgencyFormValues) => {
    const submitValues = {
      ...values,
      agency_id: values.is_master ? undefined : values.agency_id || undefined,
    } as AgencyFormValues

    if (isEdit && agency) {
      updateMutation.mutate(
        { id: agency.id, values: submitValues },
        {
          onSuccess: () => {
            notifications.show({ color: "green", title: t("common.success"), message: t("agencies.updateSuccess") })
            onSuccess()
          },
          onError: (err) => {
            notifications.show({ color: "red", title: t("common.error"), message: err.message })
          },
        },
      )
    } else {
      createMutation.mutate(submitValues, {
        onSuccess: () => {
          notifications.show({ color: "green", title: t("common.success"), message: t("agencies.createSuccess") })
          onSuccess()
        },
        onError: (err) => {
          notifications.show({ color: "red", title: t("common.error"), message: err.message })
        },
      })
    }
  }

  const handleFormSubmit = form.onSubmit(handleSubmit)
  useImperativeHandle(ref, () => ({ submit: () => handleFormSubmit() }))

  const handleLogoUpload = async (file: File | null) => {
    if (!file || !agency) return
    uploadMultimedia.mutate(
      { id: agency.id, type: "logo_path", file },
      {
        onSuccess: () => {
          notifications.show({ color: "green", title: t("common.success"), message: t("agencies.multimediaUploadSuccess") })
        },
        onError: (err) => {
          notifications.show({ color: "red", title: t("common.error"), message: err.message })
        },
      },
    )
  }

  const handleRemoveMultimedia = (type: string) => {
    if (!agency) return
    removeMultimedia.mutate(
      { id: agency.id, type },
      {
        onSuccess: () => {
          notifications.show({ color: "green", title: t("common.success"), message: t("agencies.multimediaRemoveSuccess") })
        },
      },
    )
  }

  return (
    <form onSubmit={handleFormSubmit}>
      <Tabs defaultValue="general" p="md">
        <Tabs.List mb="md">
          <Tabs.Tab value="general">{t("agencies.tabGeneral")}</Tabs.Tab>
          <Tabs.Tab value="contact">{t("agencies.tabContact")}</Tabs.Tab>
          <Tabs.Tab value="config">{t("agencies.tabConfig")}</Tabs.Tab>
          {isEdit && <Tabs.Tab value="multimedia">{t("agencies.tabMultimedia")}</Tabs.Tab>}
        </Tabs.List>

        <Tabs.Panel value="general">
          <Paper withBorder p="md" radius="md">
            <Stack gap="sm">
              <SimpleGrid cols={2} spacing="sm" verticalSpacing="sm">
                <TextInput label={t("agencies.name")} withAsterisk {...form.getInputProps("name")} />
                <TextInput label={t("agencies.socialDenomination")} {...form.getInputProps("social_denomination")} />
              </SimpleGrid>
              <SimpleGrid cols={2} spacing="sm" verticalSpacing="sm">
                <TextInput label={t("agencies.nif")} withAsterisk {...form.getInputProps("nif")} />
                <TextInput label={t("agencies.RNAVT")} withAsterisk {...form.getInputProps("RNAVT")} />
              </SimpleGrid>
              <SimpleGrid cols={2} spacing="sm" verticalSpacing="sm">
                <Select
                  label={t("agencies.district")}
                  placeholder={t("agencies.selectDistrict")}
                  data={districtOptions}
                  searchable
                  clearable
                  {...form.getInputProps("district")}
                />
                <Select
                  label={t("agencies.status")}
                  data={[
                    { value: "10", label: t("agencies.active") },
                    { value: "5", label: t("agencies.inactive") },
                  ]}
                  {...form.getInputProps("status")}
                />
              </SimpleGrid>
              <SimpleGrid cols={2} spacing="sm" verticalSpacing="sm">
                <Select
                  label={t("agencies.type")}
                  data={[
                    { value: "true", label: t("agencies.headOffice") },
                    { value: "false", label: t("agencies.branch") },
                  ]}
                  onChange={(val) => {
                    form.setFieldValue("is_master", val === "true")
                    if (val === "true") form.setFieldValue("agency_id", null)
                  }}
                  value={String(form.values.is_master)}
                />
                {!form.values.is_master && (
                  <Select
                    label={t("agencies.masterAgency")}
                    placeholder={t("agencies.selectMasterAgency")}
                    data={masterOptions}
                    searchable
                    clearable
                    {...form.getInputProps("agency_id")}
                  />
                )}
              </SimpleGrid>
            </Stack>
          </Paper>
        </Tabs.Panel>

        <Tabs.Panel value="contact">
          <Paper withBorder p="md" radius="md">
            <Stack gap="sm">
              <TextInput label={t("agencies.address")} withAsterisk {...form.getInputProps("address")} />
              <SimpleGrid cols={3} spacing="sm" verticalSpacing="sm">
                <TextInput label={t("agencies.postalCode")} withAsterisk {...form.getInputProps("postal_code")} />
                <TextInput label={t("agencies.locale")} withAsterisk {...form.getInputProps("locale")} />
                <TextInput label={t("agencies.phone")} {...form.getInputProps("phone_number")} />
              </SimpleGrid>
              <TextInput label={t("agencies.email")} withAsterisk {...form.getInputProps("email")} />
            </Stack>
          </Paper>
        </Tabs.Panel>

        <Tabs.Panel value="config">
          <Paper withBorder p="md" radius="md">
            <Stack gap="sm">
              <SimpleGrid cols={2} spacing="sm" verticalSpacing="sm">
                <TextInput label={t("agencies.subdomain")} {...form.getInputProps("subdomain")} />
                <TextInput label="Facebook URL" {...form.getInputProps("facebook_url")} />
              </SimpleGrid>
              <SimpleGrid cols={2} spacing="sm" verticalSpacing="sm">
                <TextInput label="Instagram URL" {...form.getInputProps("instagram_url")} />
                <TextInput label={t("agencies.color")} placeholder="#RRGGBB" {...form.getInputProps("color")} />
              </SimpleGrid>
              <SimpleGrid cols={2} spacing="sm" verticalSpacing="sm">
                <TextInput label={t("agencies.bdEgoi")} {...form.getInputProps("bd_egoi")} />
                <TextInput label={t("agencies.accessionDate")} type="date" {...form.getInputProps("accession_date")} />
              </SimpleGrid>
              <TextInput label={t("agencies.representative")} {...form.getInputProps("representative")} />
            </Stack>
          </Paper>
        </Tabs.Panel>

        {isEdit && agency && (
          <Tabs.Panel value="multimedia">
            <Paper withBorder p="md" radius="md">
              <Stack gap="md">
                <Text size="sm" c="dimmed">{t("agencies.multimediaHint")}</Text>

                <SimpleGrid cols={2} spacing="md">
                  <Box>
                    <Text size="sm" fw={500} mb={4}>{t("agencies.logo")}</Text>
                    <FileInput
                      placeholder={t("agencies.uploadFile")}
                      accept="image/*"
                      onChange={handleLogoUpload}
                      clearable
                    />
                    {agency.logo_url && (
                      <Group mt="xs" gap="xs">
                        <Image src={agency.logo_url} h={60} fit="contain" />
                        <ActionIcon variant="subtle" color="red" size="sm" onClick={() => handleRemoveMultimedia("logo_path")}>
                          <i className="fa fa-times" />
                        </ActionIcon>
                      </Group>
                    )}
                  </Box>

                  <Box>
                    <Text size="sm" fw={500} mb={4}>QR Facebook</Text>
                    <FileInput
                      placeholder={t("agencies.uploadFile")}
                      accept="image/*"
                      onChange={(file) => {
                        if (file) uploadMultimedia.mutate({ id: agency.id, type: "facebook_qrcode_path", file })
                      }}
                      clearable
                    />
                    {agency.facebook_qrcode_path && (
                      <Group mt="xs" gap="xs">
                        <Image src={`/img/agencies/${agency.facebook_qrcode_path}`} h={60} fit="contain" />
                        <ActionIcon variant="subtle" color="red" size="sm" onClick={() => handleRemoveMultimedia("facebook_qrcode_path")}>
                          <i className="fa fa-times" />
                        </ActionIcon>
                      </Group>
                    )}
                  </Box>

                  <Box>
                    <Text size="sm" fw={500} mb={4}>QR Subdomínio</Text>
                    <FileInput
                      placeholder={t("agencies.uploadFile")}
                      accept="image/*"
                      onChange={(file) => {
                        if (file) uploadMultimedia.mutate({ id: agency.id, type: "subdomain_qrcode_path", file })
                      }}
                      clearable
                    />
                    {agency.subdomain_qrcode_path && (
                      <Group mt="xs" gap="xs">
                        <Image src={`/img/agencies/${agency.subdomain_qrcode_path}`} h={60} fit="contain" />
                        <ActionIcon variant="subtle" color="red" size="sm" onClick={() => handleRemoveMultimedia("subdomain_qrcode_path")}>
                          <i className="fa fa-times" />
                        </ActionIcon>
                      </Group>
                    )}
                  </Box>

                  <Box>
                    <Text size="sm" fw={500} mb={4}>QR Instagram</Text>
                    <FileInput
                      placeholder={t("agencies.uploadFile")}
                      accept="image/*"
                      onChange={(file) => {
                        if (file) uploadMultimedia.mutate({ id: agency.id, type: "instagram_qrcode_path", file })
                      }}
                      clearable
                    />
                    {agency.instagram_qrcode_path && (
                      <Group mt="xs" gap="xs">
                        <Image src={`/img/agencies/${agency.instagram_qrcode_path}`} h={60} fit="contain" />
                        <ActionIcon variant="subtle" color="red" size="sm" onClick={() => handleRemoveMultimedia("instagram_qrcode_path")}>
                          <i className="fa fa-times" />
                        </ActionIcon>
                      </Group>
                    )}
                  </Box>
                </SimpleGrid>
              </Stack>
            </Paper>
          </Tabs.Panel>
        )}
      </Tabs>
    </form>
  )
})

export default AgencyFormContent
